/**
 * MADE-065: KanbanBoard
 * Unified Kanban with columns: Received | Blocked | Incomplete | In Production | QC | Pack & Ship
 *
 * MADE-BUG-08: Added HTML5 drag-and-drop to allow cards to be moved between columns.
 */

import React, { useState, useRef } from 'react';
import { Box, Typography, Stack, Badge, Button, Collapse, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, AccessTime as ClockIcon } from '@mui/icons-material';
import { KanbanCard } from './KanbanCard';
import { WaitingCard } from './WaitingCard';
import { AssemblyOrderCard } from '../AssemblyOrderCard';
import { BlockedCard } from './BlockedCard';
import type { WorkOrder } from '../../types/madebb.types';

const KANBAN_COLUMNS: { status: WorkOrder['kanbanStatus']; label: string; color: string }[] = [
  { status: 'received',      label: 'Received',      color: '#1976d2' },
  { status: 'blocked',       label: 'Blocked 🚫',        color: '#d32f2f' },
  { status: 'incomplete',    label: 'Incomplete',     color: '#ed6c02' },
  { status: 'in_production', label: 'In Production',  color: '#9c27b0' },
  { status: 'quality_check', label: 'Quality Check',  color: '#0288d1' },
  { status: 'pack_and_ship', label: 'Pack & Ship',    color: '#2e7d32' },
  { status: 'completed',     label: 'Completed',      color: '#607d8b' },
];

interface KanbanBoardProps {
  workOrders: WorkOrder[];
  onAssign: (wo: WorkOrder) => void;
  onQcPass: (wo: WorkOrder) => void;
  onQcFail: (wo: WorkOrder) => void;
  onViewPackSlip: (wo: WorkOrder) => void;
  onViewDetail: (wo: WorkOrder) => void;
  onStatusChange: (wo: WorkOrder, newStatus: WorkOrder['kanbanStatus']) => void; // For drag-drop
  onAdvance: (wo: WorkOrder, newStatus: WorkOrder['kanbanStatus']) => void; // For button clicks
  onMarkShipped: (wo: WorkOrder) => void;
  onMarkShippedRollShade: (wo: WorkOrder) => void;
  loadingActions: Record<string, boolean>;
  onAssignMachineAssembly?: (orderId: string) => void;
  onAdvanceStatusAssembly?: (orderId:string) => void;
  onQCPassAssembly?: (orderId: string) => void;
  onQCFailAssembly?: (orderId: string) => void;
  onCancel?: (wo: WorkOrder) => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  workOrders, onAssign, onQcPass, onQcFail, onViewPackSlip, onViewDetail, onStatusChange, onAdvance, onMarkShipped, onMarkShippedRollShade, loadingActions,
  onAssignMachineAssembly, onAdvanceStatusAssembly, onQCPassAssembly, onQCFailAssembly, onCancel,
}) => {
  const dragId = useRef<string | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  // MADE-094: "Waiting on Production" sub-section — collapsed by default
  const [waitingExpanded, setWaitingExpanded] = useState(false);

  const ordersForStatus = (status: WorkOrder['kanbanStatus']) =>
    workOrders.filter(wo => wo.kanbanStatus === status);

  // MADE-094: find the production card for a waiting order (cross-reference by id)
  const findLinkedProductionCard = (waitingOrder: WorkOrder): WorkOrder | undefined =>
    workOrders.find(wo => wo.id === waitingOrder.waitingOnProductionOrderId);

  const handleDragStart = (woId: string) => {
    dragId.current = woId;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetStatus: WorkOrder['kanbanStatus']) => {
    e.preventDefault();
    if (!dragId.current) return;
    const wo = workOrders.find(w => w.id === dragId.current);
    if (wo && wo.kanbanStatus !== targetStatus) {
      onStatusChange(wo, targetStatus);
    }
    dragId.current = null;
  };

  const activeColumns = KANBAN_COLUMNS.filter(c => c.status !== 'completed');
  const completedColumn = KANBAN_COLUMNS.find(c => c.status === 'completed');
  const completedOrders = completedColumn ? ordersForStatus(completedColumn.status) : [];

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        pb: 2,
        alignItems: 'flex-start',
      }}
    >
      {activeColumns.map(col => {
        // MADE-094: for the Received column, split out waiting_on_production cards
        const waitingOrders = col.status === 'received'
          ? workOrders.filter(wo => wo.kanbanStatus === 'waiting_on_production')
          : [];
        const colOrders = col.status === 'received'
          ? ordersForStatus('received') // excludes waiting_on_production — they have their own sub-section
          : ordersForStatus(col.status);
        const totalReceivedCount = colOrders.length + waitingOrders.length;

        return (
          <Box
            key={col.status}
            data-testid={col.status === 'blocked' ? 'blocked-column' : undefined}
            sx={{ minWidth: 280, maxWidth: 320, flexShrink: 0 }}
            onDragOver={handleDragOver}
            onDrop={e => handleDrop(e, col.status)}
          >
            {/* Column header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                bgcolor: col.color,
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: '8px 8px 0 0',
              }}
            >
              <Typography fontWeight={700} fontSize={14}>
                {col.label}
              </Typography>
              <Badge
                badgeContent={col.status === 'received' ? totalReceivedCount : colOrders.length}
                color="default"
                sx={{ '& .MuiBadge-badge': { bgcolor: 'rgba(255,255,255,0.8)', color: col.color } }}
              >
                <Box />
              </Badge>
            </Stack>

            {/* Cards */}
            <Box
              sx={{
                bgcolor: 'grey.100',
                borderRadius: '0 0 8px 8px',
                p: 1,
                minHeight: 120,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {colOrders.length === 0 && waitingOrders.length === 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ p: 1, textAlign: 'center' }}>
                  No orders
                </Typography>
              )}
              {colOrders.map(wo => {
                if (wo.type === 'production' && wo.kanbanStatus === 'blocked') {
                  return (
                    <Box
                      key={wo.id}
                      draggable
                      onDragStart={() => handleDragStart(wo.id)}
                      sx={{ cursor: 'grab' }}
                    >
                      <BlockedCard
                        workOrder={wo}
                        onViewDetail={onViewDetail}
                      />
                    </Box>
                  );
                }
                if (wo.type === 'assembly') {
                  return (
                    <Box
                      key={wo.id}
                      draggable
                      onDragStart={() => handleDragStart(wo.id)}
                      sx={{ cursor: 'grab' }}
                    >
                      <AssemblyOrderCard
                        order={wo._raw as any}
                        onAssignMachine={onAssignMachineAssembly!}
                        onAdvanceStatus={onAdvanceStatusAssembly!}
                        onQCPass={onQCPassAssembly!}
                        onQCFail={onQCFailAssembly!}
                      />
                    </Box>
                  );
                }
                return (
                  <Box
                    key={wo.id}
                    draggable
                    onDragStart={() => handleDragStart(wo.id)}
                    sx={{ cursor: 'grab' }}
                  >
                    <KanbanCard
                      workOrder={wo}
                      onAssign={onAssign}
                      onQcPass={onQcPass}
                      onQcFail={onQcFail}
                      onViewPackSlip={onViewPackSlip}
                      onViewDetail={onViewDetail}
                      onAdvance={onAdvance}
                      onMarkShipped={onMarkShipped}
                      onMarkShippedRollShade={onMarkShippedRollShade}
                      onCancel={onCancel}
                      loadingActions={loadingActions}
                    />
                  </Box>
                );
              })}

              {/* MADE-094: "Waiting on Production" collapsible sub-section (Received column only) */}
              {col.status === 'received' && waitingOrders.length > 0 && (
                <Box
                  data-testid="waiting-on-production-section"
                  sx={{
                    mt: colOrders.length > 0 ? 1 : 0,
                    border: '1px solid #ffe0b2',
                    borderRadius: 1,
                    bgcolor: '#fff3e0',
                    overflow: 'hidden',
                  }}
                >
                  {/* Sub-section header — clickable to expand/collapse */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#ffe0b2' },
                    }}
                    onClick={() => setWaitingExpanded(prev => !prev)}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <ClockIcon sx={{ fontSize: 14, color: '#f57c00' }} />
                      <Typography variant="caption" fontWeight={700} color="#e65100">
                        Waiting on Production ({waitingOrders.length})
                      </Typography>
                    </Stack>
                    <IconButton size="small" sx={{ p: 0.25, color: '#e65100' }}>
                      {waitingExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Stack>

                  {/* Expandable list of WaitingCards */}
                  <Collapse in={waitingExpanded}>
                    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {waitingOrders.map(wo => {
                        const linked = findLinkedProductionCard(wo);
                        return (
                          <WaitingCard
                            key={wo.id}
                            workOrder={wo}
                            linkedProductionOrderId={wo.waitingOnProductionOrderId}
                            linkedProductionStatus={linked?.kanbanStatus ?? wo.waitingProductionStatus}
                            onViewDetail={onViewDetail}
                          />
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}

      {/* Completed Column */}
      <Box sx={{ minWidth: showCompleted ? 280 : 120, maxWidth: showCompleted ? 320 : 120, flexShrink: 0, transition: 'min-width 0.3s' }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setShowCompleted(!showCompleted)}
          sx={{ mb: 1 }}
        >
          {showCompleted ? 'Hide' : 'Show'} Completed ({completedOrders.length})
        </Button>
        {showCompleted && completedColumn && (
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                bgcolor: completedColumn.color,
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: '8px 8px 0 0',
              }}
            >
              <Typography fontWeight={700} fontSize={14}>
                {completedColumn.label}
              </Typography>
              <Badge
                badgeContent={completedOrders.length}
                color="default"
                sx={{ '& .MuiBadge-badge': { bgcolor: 'rgba(255,255,255,0.8)', color: completedColumn.color } }}
              >
                <Box />
              </Badge>
            </Stack>
            <Box
              sx={{
                bgcolor: 'grey.100',
                borderRadius: '0 0 8px 8px',
                p: 1,
                minHeight: 120,
                maxHeight: '70vh',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {completedOrders.map(wo => (
                <KanbanCard
                  key={wo.id}
                  workOrder={wo}
                  onViewDetail={onViewDetail}
                  // These are no-ops for completed cards
                  onAssign={() => {}}
                  onQcPass={() => {}}
                  onQcFail={() => {}}
                  onViewPackSlip={() => {}}
                  onAdvance={() => {}}
                  onMarkShipped={() => {}}
                  onMarkShippedRollShade={() => {}}
                  loadingActions={{}}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
